/**
 * App-local defer wrapper for @directv-digital/dds.services.analytics-service.
 *
 * webpack resolves the BARE package specifier to this module (exact-match `$`
 * alias in next.config.js, client bundles only — same pattern as the
 * fusion.atoms.icons shim) and jest mirrors it via moduleNameMapper, so all
 * consumer files get the deferred fire* functions with zero import churn.
 * This module reaches the real package through its /dist/index subpath, which
 * the exact-match alias does not intercept.
 *
 * Scope note: because the alias also rewrites bare imports made INSIDE
 * node_modules, this defers analytics fired by @directv-digital components
 * themselves (dtv-button CTA clicks, error-display, impressions, …), not just
 * first-party app call sites. That is intended — those calls are the same
 * tap-frame trackEvent surface.
 *
 * Every event-emitting export is wrapped in runAnalyticsTask (post-paint FIFO,
 * kill switch = the remote per-env toggle in
 * lib/cache/in-memory-cache/analytics-flags.ts — off means fully synchronous
 * legacy behavior). Pure helpers, constants, enums and types pass through unchanged;
 * getAnalyticsAttrs / getAnalyticsDataAttrs return values used during render
 * and MUST stay synchronous. handleLinkClick also passes through: it defers
 * itself via an internal setTimeout(0), and queueing it would let a pagehide
 * flush start that timer after unload — the event would be lost.
 *
 * Related but different: the fireEvent/DataMappingInterface path (see
 * ReportingService) can also arm a timer inside a queued task, but only when
 * the SSAF global hasn't loaded yet — that 50ms polling already loses events
 * across navigation today, so the queue changes nothing there. handleLinkClick
 * would arm its timer on EVERY call, turning a working path into a lossy one;
 * that is why it alone is excluded.
 *
 * Timing asymmetry (accepted): the wrapped functions run whole at flush time,
 * so ones that read live browser state (fireErrorPageViewEvent reads
 * window.location.href, fireModalPageLoadEvent reads document.title) see the
 * page one frame later than the legacy sync path — a same-task pushState/title
 * change lands in the event. App-side call sites in this PR snapshot their
 * payloads (timestamps etc.) before enqueueing; DDS internals cannot be
 * snapshotted from here without vendoring them.
 *
 * The wrap list is pinned to the package's export surface. Drift is NOT
 * harmless: this module replaces the whole package, so a package export
 * missing here is `undefined` for every consumer — a TypeError where it is
 * called. And the package version floats (~ range, no committed lockfile), so
 * drift can arrive with any install. Do NOT rely on the `resolutions` entry to
 * hold a version: it is not applied on the yarn install path (this repo only
 * enforces resolutions through the `generate:package-lock` script, which runs
 * npm-force-resolutions). Observed directly — with `resolutions: 4.3.93` set,
 * `yarn` installed 4.3.94. The direct dependency (`~4.3.95` at the time of
 * writing) is what decides, and it admits any later 4.3.x, which is how
 * publishing 4.3.93 broke devdeploy/next before the matching wrapper entries
 * landed. Two guards: the export-surface test in
 * dds-analytics-wrapper.spec.ts (local runs) and
 * config/analytics-defer-surface-check.js, which fails `next build`/`next dev`
 * (tsc won't catch it — ignoreBuildErrors — and CI does not run jest).
 */
import {
  fireAddToCartEvent as ddsFireAddToCartEvent,
  fireApiResponseEventGA as ddsFireApiResponseEventGA,
  fireCartSummary as ddsFireCartSummary,
  fireCmsTypePageLoadEvent as ddsFireCmsTypePageLoadEvent,
  fireComponentDisplayedEvent as ddsFireComponentDisplayedEvent,
  fireCtaClickEventGA4 as ddsFireCtaClickEventGA4,
  fireCustomClickMultiSearchGA as ddsFireCustomClickMultiSearchGA,
  fireCustomLinkClickForLabels as ddsFireCustomLinkClickForLabels,
  fireCustomLinkClickGA as ddsFireCustomLinkClickGA,
  fireCustomLinkClickImpressions as ddsFireCustomLinkClickImpressions,
  fireDataLayerEvent as ddsFireDataLayerEvent,
  fireEpsilonEvent as ddsFireEpsilonEvent,
  fireErrorDisplayedGA as ddsFireErrorDisplayedGA,
  fireErrorPageViewEvent as ddsFireErrorPageViewEvent,
  fireFormAction as ddsFireFormAction,
  fireGa4HorizontalPlanCard as ddsFireGa4HorizontalPlanCard,
  fireGa4HorizontalPlanCardTrigger as ddsFireGa4HorizontalPlanCardTrigger,
  fireGa4ItemList as ddsFireGa4ItemList,
  fireGa4ProductImpression as ddsFireGa4ProductImpression,
  fireHeaderLinkClickGA as ddsFireHeaderLinkClickGA,
  fireImpression as ddsFireImpression,
  fireLeadCaptureEventGA as ddsFireLeadCaptureEventGA,
  fireLinkClickEvent as ddsFireLinkClickEvent,
  fireModalOpenCloseEventGA as ddsFireModalOpenCloseEventGA,
  fireModalPageLoadEvent as ddsFireModalPageLoadEvent,
  fireModifiedComponentTypePageLoadEvent as ddsFireModifiedComponentTypePageLoadEvent,
  firePageLoadEvent as ddsFirePageLoadEvent,
  fireQuizEventGA as ddsFireQuizEventGA,
  fireRemoveFromCartEvent as ddsFireRemoveFromCartEvent,
  fireSearchEvent as ddsFireSearchEvent,
  fireSearchImpressions as ddsFireSearchImpressions,
  fireSelectItem as ddsFireSelectItem,
  fireTVPromiseGA as ddsFireTVPromiseGA,
  fireTVPromiseZipSearchCaptureGA as ddsFireTVPromiseZipSearchCaptureGA,
  fireTabHandleChange as ddsFireTabHandleChange,
  fireViewItemList as ddsFireViewItemList,
  fireVideoPlayEvent as ddsFireVideoPlayEvent,
  fireVideoPlayerGA as ddsFireVideoPlayerGA,
  fireWidgetCustomLinkClickGA as ddsFireWidgetCustomLinkClickGA,
  fireZoovuEventGA as ddsFireZoovuEventGA,
  sendImpressions as ddsSendImpressions
} from "@directv-digital/dds.services.analytics-service/dist/index";
import { runAnalyticsTask } from "./analytics-defer";

// Fire-and-forget by contract: every wrapped fire* is void. The queue defers
// execution, so a return value could never reach the caller anyway. If a
// future upstream fire* returns a value that callers consume, it must NOT
// ride this wrapper — the surface check guards export existence, not return
// types, so that break would be silent.
const deferred =
  <TArgs extends unknown[]>(fire: (...args: TArgs) => void) => (...args: TArgs): void => {
    runAnalyticsTask(() => fire(...args));
  };

// component-events
export const fireAddToCartEvent = deferred(ddsFireAddToCartEvent);
export const fireApiResponseEventGA = deferred(ddsFireApiResponseEventGA);
// WAT-11358. Queued like every other fire*: it pushes the GA4 ecommerce reset
// and the payload in one call, so both land in the same task and no other event
// can interleave between them. buildCartSummaryPayload stays synchronous below —
// the caller consumes its return value.
export const fireCartSummary = deferred(ddsFireCartSummary);
export const fireCtaClickEventGA4 = deferred(ddsFireCtaClickEventGA4);
export const fireCustomClickMultiSearchGA = deferred(
  ddsFireCustomClickMultiSearchGA
);
export const fireCustomLinkClickImpressions = deferred(
  ddsFireCustomLinkClickImpressions
);
export const fireErrorDisplayedGA = deferred(ddsFireErrorDisplayedGA);
export const fireErrorPageViewEvent = deferred(ddsFireErrorPageViewEvent);
export const fireGa4HorizontalPlanCard = deferred(ddsFireGa4HorizontalPlanCard);
export const fireGa4HorizontalPlanCardTrigger = deferred(
  ddsFireGa4HorizontalPlanCardTrigger
);
export const fireGa4ItemList = deferred(ddsFireGa4ItemList);
export const fireGa4ProductImpression = deferred(ddsFireGa4ProductImpression);
export const fireHeaderLinkClickGA = deferred(ddsFireHeaderLinkClickGA);
export const fireLeadCaptureEventGA = deferred(ddsFireLeadCaptureEventGA);
export const fireQuizEventGA = deferred(ddsFireQuizEventGA);
export const fireRemoveFromCartEvent = deferred(ddsFireRemoveFromCartEvent);
export const fireSearchEvent = deferred(ddsFireSearchEvent);
export const fireSearchImpressions = deferred(ddsFireSearchImpressions);
export const fireTVPromiseGA = deferred(ddsFireTVPromiseGA);
export const fireTVPromiseZipSearchCaptureGA = deferred(
  ddsFireTVPromiseZipSearchCaptureGA
);
export const fireTabHandleChange = deferred(ddsFireTabHandleChange);
export const fireVideoPlayEvent = deferred(ddsFireVideoPlayEvent);
export const fireVideoPlayerGA = deferred(ddsFireVideoPlayerGA);
export const fireWidgetCustomLinkClickGA = deferred(
  ddsFireWidgetCustomLinkClickGA
);
export const fireZoovuEventGA = deferred(ddsFireZoovuEventGA);

// core-events
export const fireCmsTypePageLoadEvent = deferred(ddsFireCmsTypePageLoadEvent);
export const fireComponentDisplayedEvent = deferred(
  ddsFireComponentDisplayedEvent
);
export const fireCustomLinkClickForLabels = deferred(
  ddsFireCustomLinkClickForLabels
);
export const fireCustomLinkClickGA = deferred(ddsFireCustomLinkClickGA);
export const fireEpsilonEvent = deferred(ddsFireEpsilonEvent);
export const fireFormAction = deferred(ddsFireFormAction);
export const fireImpression = deferred(ddsFireImpression);
export const fireLinkClickEvent = deferred(ddsFireLinkClickEvent);
export const fireModalOpenCloseEventGA = deferred(ddsFireModalOpenCloseEventGA);
export const fireModalPageLoadEvent = deferred(ddsFireModalPageLoadEvent);
export const fireModifiedComponentTypePageLoadEvent = deferred(
  ddsFireModifiedComponentTypePageLoadEvent
);
export const firePageLoadEvent = deferred(ddsFirePageLoadEvent);
export const fireSelectItem = deferred(ddsFireSelectItem);
export const fireViewItemList = deferred(ddsFireViewItemList);
export const sendImpressions = deferred(ddsSendImpressions);

// ga
export const fireDataLayerEvent = deferred(ddsFireDataLayerEvent);

// Synchronous passthroughs — helpers, constants, enums (and render-time attr
// builders whose return values are consumed immediately). handleLinkClick is
// deliberately NOT queued: it already defers itself via setTimeout(0), and a
// pagehide flush would arm that timer after unload, losing the event.
export {
  EventActions,
  EventCode,
  LINK_POSITION_BODY,
  ReportingDefaults,
  buildCartSummaryPayload,
  constructFullUrl,
  fixAnalyticsValues,
  getAnalyticsAttrs,
  getAnalyticsDataAttrs,
  handleLinkClick,
  tagCookieData,
  titleCase
} from "@directv-digital/dds.services.analytics-service/dist/index";
export type {
  Analytics,
  GoogleAnalytics,
  ReportingDefaultsData
} from "@directv-digital/dds.services.analytics-service/dist/index";
